import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Course, SubCategory, HomepageSettings } from '../types';
import { handleFirestoreError } from '../utils/error-handler';
import { OperationType } from '../types';

interface CourseContextType {
  courses: Course[];
  subCategories: SubCategory[];
  homepageSettings: HomepageSettings | null;
  loading: boolean;
  refreshData: () => void;
}

const CourseContext = createContext<CourseContextType>({
  courses: [],
  subCategories: [],
  homepageSettings: null,
  loading: true,
  refreshData: () => {},
});

export const useCourses = () => useContext(CourseContext);

export const CourseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [homepageSettings, setHomepageSettings] = useState<HomepageSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [subCategoriesLoaded, setSubCategoriesLoaded] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const refreshData = () => {
    console.log("[CourseContext] Manually refreshing data listeners...");
    setCoursesLoaded(false);
    setSubCategoriesLoaded(false);
    setSettingsLoaded(false);
    setLoading(true);
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    console.log("[CourseContext] Starting data listeners (refreshKey:", refreshKey, ")");
    const qCourses = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    const qSubCategories = query(collection(db, 'subCategories'), orderBy('createdAt', 'asc'));
    const qSettings = query(collection(db, 'settings'));
    
    const unsubCourses = onSnapshot(qCourses, (snapshot) => {
      console.log(`[CourseContext] Courses snapshot: ${snapshot.size} docs`);
      setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Course[]);
      setCoursesLoaded(true);
    }, (error) => {
      console.error("[CourseContext] Courses listener error:", error);
      try {
        handleFirestoreError(error, OperationType.LIST, 'courses');
      } catch (e) {
        // Catch the throw from handleFirestoreError to prevent crashing the context
      }
      setCoursesLoaded(true);
    });

    const unsubSubCategories = onSnapshot(qSubCategories, (snapshot) => {
      console.log(`[CourseContext] SubCategories snapshot: ${snapshot.size} docs`);
      setSubCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SubCategory[]);
      setSubCategoriesLoaded(true);
    }, (error) => {
      console.error("[CourseContext] SubCategories listener error:", error);
      try {
        handleFirestoreError(error, OperationType.LIST, 'subCategories');
      } catch (e) {
      }
      setSubCategoriesLoaded(true);
    });

    const unsubSettings = onSnapshot(qSettings, (snapshot) => {
      console.log(`[CourseContext] Settings snapshot: ${snapshot.empty ? 'empty' : 'exists'}`);
      if (!snapshot.empty) {
        setHomepageSettings({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as HomepageSettings);
      }
      setSettingsLoaded(true);
    }, (error) => {
      console.error("[CourseContext] Settings listener error:", error);
      try {
        handleFirestoreError(error, OperationType.LIST, 'settings');
      } catch (e) {
      }
      setSettingsLoaded(true);
    });

    return () => {
      unsubCourses();
      unsubSubCategories();
      unsubSettings();
    };
  }, [refreshKey]);

  useEffect(() => {
    if (coursesLoaded && subCategoriesLoaded && settingsLoaded) {
      setLoading(false);
    }
  }, [coursesLoaded, subCategoriesLoaded, settingsLoaded]);

  return (
    <CourseContext.Provider value={{ 
      courses, 
      subCategories, 
      homepageSettings, 
      loading, 
      refreshData 
    }}>
      {children}
    </CourseContext.Provider>
  );
};
